import type { Result, ResultValueProp } from '@de/types/result';
import type {
  OfferingDetailOrg,
  OfferingDetailValueProp,
  OfferingDetailProgramFlowEntry,
  OfferingDetailSpotlight,
} from '@de/types/offerings';

/**
 * Subset of `Result` carrying the per-offering drawer-detail fields the
 * lead-form drawer renders (TCPA, secondary TCPA, value props, program flow,
 * program dates, confirmation copy, spotlight). The match card projection
 * leaves these empty; they come from the offering detail-by-id view.
 *
 * Card-identity fields (`schoolId`, `programId`, `schoolSlug`,
 * `applicationCtaURL`, …) are deliberately excluded so a merge can never
 * clobber the card's identity/CTA.
 */
export type ResultDetailFields = Partial<
  Pick<
    Result,
    | 'tcpa'
    | 'tcpaSecondParagraph'
    | 'valueProps'
    | 'programFlow'
    | 'programDates'
    | 'confirmationShortIntroText'
    | 'confirmationText'
    | 'spotlight'
  >
>;

/**
 * Map the offering detail-by-id org projection (`GET /v1/offerings/:id` →
 * `data.organizations[0]`, the flat `OrgDetailDataDto` shape) onto the
 * corresponding `Result` drawer-detail fields.
 *
 * Only fields actually present on the detail org are returned, so a
 * downstream `{ ...cardResult, ...mapped }` spread never overwrites a
 * card-provided value with an empty/undefined detail value (non-destructive
 * merge — keeps card identity).
 */
export function mapOfferingDetailToResultDetailFields(
  orgDetail: OfferingDetailOrg | null | undefined,
): ResultDetailFields {
  if (!orgDetail || typeof orgDetail !== 'object') {
    return {};
  }

  const fields: ResultDetailFields = {};

  if (isNonEmptyString(orgDetail.tcpa)) {
    fields.tcpa = orgDetail.tcpa;
  }
  if (isNonEmptyString(orgDetail.tcpaSecondParagraph)) {
    fields.tcpaSecondParagraph = orgDetail.tcpaSecondParagraph;
  }

  const valueProps = mapValueProps(orgDetail.valueProps);
  if (valueProps.length > 0) {
    fields.valueProps = valueProps;
  }

  const programFlow = mapProgramFlow(orgDetail.programFlow);
  if (programFlow.length > 0) {
    fields.programFlow = programFlow;
  }

  const programDates = mapProgramDates(orgDetail.formDates);
  if (programDates) {
    fields.programDates = programDates;
  }

  const shortIntro = orgDetail.confirmation?.shortIntroText;
  if (isNonEmptyString(shortIntro)) {
    fields.confirmationShortIntroText = shortIntro;
  }
  const confirmationText = orgDetail.confirmation?.text;
  if (isNonEmptyString(confirmationText)) {
    fields.confirmationText = confirmationText;
  }

  const spotlight = mapSpotlight(orgDetail.spotlight);
  if (spotlight) {
    fields.spotlight = spotlight;
  }

  // Spotlight parity is unresolved: offerings returns a flattened
  // `spotlight { header, blocks }` while the legacy drawer renders a bulleted
  // highlights list that may be driven by `valueProps`. We map the
  // structurally-aligned header/blocks; revisit once the bullet source is
  // confirmed. Non-blocking for core wiring.
  //
  // The detail org also carries `quickFacts[]` and flat institutional stats
  // (`accreditator`, `levelOfInstitution`, `percentOnlineEnrollment`).
  // `Result` has no generic quick-facts field, and the stats are card-surface
  // data already populated from the match card, so they are not remapped here.

  return fields;
}

/**
 * True when the resolved offering-detail org corresponds to the open card
 * selection. The detail source is id-keyed by the DSM
 * (`{{formState.selectedOfferingId}}`), so a resolved payload already maps
 * to the current selection; this is defense-in-depth against merging a
 * stale detail from a previous selection during the loading window.
 *
 * Compares the legacy EduDirect school id (`externalIds.edudirectId` ↔
 * `Result.schoolId`) and the school slug (`slug` ↔ `Result.schoolSlug`),
 * but only for identifiers present on **both** sides. Accepts as soon as
 * **any** comparable identifier matches (OR semantics — an id match wins even
 * if the slug differs, e.g. after a slug rename), and rejects only when every
 * comparable identifier disagrees (the stale-detail case). When neither side
 * offers a comparable identifier — the detail carries none, or the card is a
 * forward-identity `/match` row with empty `schoolId`/`schoolSlug` — there is
 * nothing to compare, so it falls back to `true` and relies on the DSM's
 * id-keying. Gating on a bare-`edudirectId`/`slug` presence check would
 * wrongly reject every enrichment for those sparse `/match` rows.
 */
export function offeringDetailMatchesResult(
  orgDetail: OfferingDetailOrg | null | undefined,
  result: Pick<Result, 'schoolId' | 'schoolSlug'> | null | undefined,
): boolean {
  if (!orgDetail || !result) {
    return false;
  }
  const edudirectId = orgDetail.externalIds?.edudirectId;
  const slug = orgDetail.slug;
  const canCompareId =
    isNonEmptyString(edudirectId) && isNonEmptyString(result.schoolId);
  const canCompareSlug =
    isNonEmptyString(slug) && isNonEmptyString(result.schoolSlug);

  if (!canCompareId && !canCompareSlug) {
    return true;
  }
  if (canCompareId && edudirectId === result.schoolId) {
    return true;
  }
  if (canCompareSlug && slug === result.schoolSlug) {
    return true;
  }
  return false;
}

/**
 * Resolve the offering-detail org object from a resolved DSM source value,
 * tolerating either `responsePath` choice:
 *   - the org object directly (`responsePath: "data.organizations.0"`, the
 *     authored default), or
 *   - the envelope (`responsePath: "data"` → `{ organizations: [...] }`), or
 *   - the raw HTTP body (`{ data: { organizations: [...] } }`) when no
 *     `responsePath` extraction ran (e.g. comparison hydrate).
 *
 * Resolution rules:
 *   - non-object input → `undefined` (caller degrades to a card-only result);
 *   - `{ data: … }` wrapper → recurse into `data` (single unwrap);
 *   - an envelope (`organizations` is an array) → its first organization, or
 *     `undefined` when the array is empty or its first entry is not an object
 *     (so an envelope is never silently mapped to no fields);
 *   - any other object → returned as-is, treated as the org projection. The
 *     flat org DTO has no reliable discriminator to validate structurally, so
 *     a misconfigured `responsePath` that yields an unexpected object maps to
 *     empty fields, which the per-field mappers below drop (card-only).
 */
export function resolveOfferingDetailOrg(
  data: unknown,
): OfferingDetailOrg | undefined {
  if (!data || typeof data !== 'object') {
    return undefined;
  }

  const record = data as {
    data?: unknown;
    organizations?: unknown;
  };

  // Raw GET /v1/offerings/:id body is `{ data: { organizations: [...] } }`.
  // DSM usually peels `data` via responsePath; callers that fetch without a
  // path (comparison hydrate) still need this unwrap.
  if (
    'data' in record &&
    record.data &&
    typeof record.data === 'object' &&
    !Array.isArray(record.organizations)
  ) {
    return resolveOfferingDetailOrg(record.data);
  }

  const organizations = record.organizations;
  if (Array.isArray(organizations)) {
    const first = organizations[0];
    return first && typeof first === 'object'
      ? (first as OfferingDetailOrg)
      : undefined;
  }
  return data as OfferingDetailOrg;
}

function mapValueProps(
  valueProps: OfferingDetailValueProp[] | undefined,
): ResultValueProp[] {
  if (!Array.isArray(valueProps)) {
    return [];
  }
  return valueProps
    .filter((vp) => vp && isNonEmptyString(vp.title))
    .map((vp) => ({
      title: vp.title,
      description: vp.description ?? '',
      icon: vp.icon ?? '',
    }));
}

function mapProgramFlow(
  programFlow: OfferingDetailProgramFlowEntry[] | undefined,
): NonNullable<Result['programFlow']> {
  if (!Array.isArray(programFlow)) {
    return [];
  }
  return (
    programFlow
      .map((entry) => {
        const mapped: { providerProgramIdArray?: string[]; tcpa?: string } = {};
        // offerings-service supplies a comma-separated string; `Result`'s
        // `programFlow[].providerProgramIdArray` is a string array.
        if (isNonEmptyString(entry.providerProgramIdArray)) {
          const ids = entry.providerProgramIdArray
            .split(',')
            .map((id) => id.trim())
            .filter((id) => id.length > 0);
          if (ids.length > 0) {
            mapped.providerProgramIdArray = ids;
          }
        }
        if (isNonEmptyString(entry.tcpa)) {
          mapped.tcpa = entry.tcpa;
        }
        return mapped;
      })
      // Drop rows that carry no usable field so the merge stays
      // non-destructive (an array of `{}` would otherwise overwrite a
      // card-provided `programFlow`).
      .filter((mapped) => Object.keys(mapped).length > 0)
  );
}

function mapProgramDates(
  formDates: OfferingDetailOrg['formDates'],
): Result['programDates'] | undefined {
  if (!formDates) {
    return undefined;
  }
  // The detail projection exposes undergrad / grad arrays of
  // `{ startDate, showDateOn? }`; `Result.programDates` is a single
  // `{ registerDate?, startDate? }`. Take the first available start date
  // (undergrad preferred). The offerings shape carries no register date,
  // so `registerDate` is left unset.
  const first = formDates.undergrad?.[0] ?? formDates.grad?.[0];
  if (!first || !isNonEmptyString(first.startDate)) {
    return undefined;
  }
  return { startDate: first.startDate };
}

function mapSpotlight(
  spotlight: OfferingDetailSpotlight | undefined,
): Result['spotlight'] | undefined {
  if (!spotlight || typeof spotlight !== 'object') {
    return undefined;
  }
  const blocks = Array.isArray(spotlight.blocks)
    ? spotlight.blocks
        .map((block) => {
          const list = Array.isArray(block.list)
            ? block.list
                .map((item) => item?.text)
                .filter((text): text is string => isNonEmptyString(text))
                .map((text) => ({ text }))
            : [];
          return {
            ...(isNonEmptyString(block.title) ? { title: block.title } : {}),
            ...(isNonEmptyString(block.image) ? { image: block.image } : {}),
            ...(list.length > 0 ? { list } : {}),
          };
        })
        // Drop structurally-empty blocks (no title, image, or list) so an
        // array of `{}` never gets merged onto `Result.spotlight`.
        .filter((block) => Object.keys(block).length > 0)
    : undefined;

  const hasHeader = isNonEmptyString(spotlight.header);
  if (!hasHeader && (!blocks || blocks.length === 0)) {
    return undefined;
  }
  return {
    ...(hasHeader ? { header: spotlight.header } : {}),
    ...(blocks && blocks.length > 0 ? { blocks } : {}),
  };
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}
